import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { nestApi } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Info } from "lucide-react";
import { toast } from "sonner";
import PaymentPage from "@/pages/PaymentPage";

const countries = [
  "Italy", "Germany", "France", "Spain", "United Kingdom", "United States",
  "Switzerland", "Austria", "Netherlands", "Belgium", "Portugal", "Greece",
];

const PAYMENT_METHODS = [
  { id: "bank_transfer", label: "Bank Transfer" },
  { id: "paypal", label: "PayPal" },
  { id: "revolut", label: "Revolut" },
];

function HelpText({ text }) {
  return (
    <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
      <Info className="w-3 h-3 shrink-0" /> {text}
    </p>
  );
}

function PaymentsTab() {
  const { t } = useTranslation();
  const [records, setRecords] = useState([]);
  const [financeData, setFinanceData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFinances = async () => {
      try {
        const { data } = await nestApi.get("/finances");
        setRecords(data.records || []);
        setFinanceData(data || {});
      } catch {
        toast.error("Failed to load payment records");
        setRecords([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFinances();
  }, []);

  const getStatusStyle = (status) => {
    if (status === "Processed") return "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20";
    return "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#1A1B2E] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="payments-history-tab">
      {/* Summary Cards - removed Payment Via */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Total Earnings</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{financeData.currency} {financeData.total_earnings}</p>
        </div>
        <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Transactions</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{records.length}</p>
        </div>
      </div>

      {/* Finance Table - removed Type and Receipt columns */}
      <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 border-b border-slate-200">
                <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">#</TableHead>
                <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">City</TableHead>
                <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tour Date</TableHead>
                <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</TableHead>
                <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Payment Date</TableHead>
                <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((r, i) => (
                <TableRow key={r.id} className="table-row-hover" data-testid={`finance-row-${i}`}>
                  <TableCell className="px-4 py-4 text-sm text-slate-600">{i + 1}</TableCell>
                  <TableCell className="px-4 py-4 text-sm font-medium text-slate-900">{r.tour_city}</TableCell>
                  <TableCell className="px-4 py-4 text-sm text-slate-600">{r.tour_date}</TableCell>
                  <TableCell className="px-4 py-4 text-sm font-semibold text-slate-900">{r.currency} {r.amount}</TableCell>
                  <TableCell className="px-4 py-4 text-sm text-slate-600">{r.payment_date}</TableCell>
                  <TableCell className="px-4 py-4">
                    <span className={`status-badge ${getStatusStyle(r.status)}`}>{r.status}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

export default function FinancePage() {
  const { t } = useTranslation();

  return (
    <div className="animate-slideUp space-y-6" data-testid="finance-page">
      <p className="text-sm text-slate-500">Manage your payment method and view processed payments</p>
      <Tabs defaultValue="payment" data-testid="finance-tabs">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-xl">
          <TabsTrigger value="payment" data-testid="tab-payment-method" className="rounded-lg data-[state=active]:bg-[#1A1B2E] data-[state=active]:text-white">
            Payment Method
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments-history" className="rounded-lg data-[state=active]:bg-[#1A1B2E] data-[state=active]:text-white">
            Payments
          </TabsTrigger>
        </TabsList>

        <TabsContent value="payment" className="mt-6">
          <PaymentPage />
        </TabsContent>
        <TabsContent value="payments" className="mt-6">
          <PaymentsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
